<template>
  <v-dialog
    v-model="show"
    persistent
    scrollable
    max-width="1100px"
    transition="dialog-transition"
    :hide-overlay="headless"
  >
    <v-card v-show="!headless">
      <v-card-title class="grey lighten-3 py-0">
        <v-row align="center" dense>
          <v-icon class="draggable">mdi-drag</v-icon>
          <v-col class="px-0 pb-0">
            <v-container v-if="switchService" py-0>
              <itinerary-builder-item-quick-add
                :compact="true"
                :agent-id="headerProps.agentId"
                :start-date="startDate"
                :destination-id="headerProps.destinationId"
                :adults="headerProps.adults"
                :children="headerProps.children"
                :infants="headerProps.infants"
                :duration="headerProps.duration"
                :department-name="headerProps.department"
                :focus-on-select="focusOnQuickAdd"
                :service-type="serviceType"
                :edited-item="selectedItem"
                @onSelect="onServiceSelect"
                @showAdhoc="showAdhocFromQuickAdd"
                @supplier-edit="onSupplierEdit"
              ></itinerary-builder-item-quick-add>
            </v-container>
            <v-container v-else py-4>
              <v-btn
                icon
                @click="onServiceEdit"
                color="info"
                outlined
                :disabled="selectedItem && selectedItem.hasPurchaseInvoice"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              {{ label }}
            </v-container>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-tabs right v-model="currentTab">
          <v-tab key="details" @change="checkDuration">Details</v-tab>
          <v-tab key="remarks" @change="onRemarks">Remarks</v-tab>
        </v-tabs>
        <v-tabs-items v-model="currentTab">
          <v-tab-item key="details">
            <v-container grid-list-sm fluid wrap class="pb-0 pt-0">
              <v-layout row wrap align-baseline>
                <v-flex xs12 sm4>
                  <date-picker
                    :customLabel="startDateLabel"
                    :gt-date-string="startDate"
                    :rangeStart="startDateRangeStart"
                    :rangeEnd="startDateRangeEnd"
                    @onDateSelected="onStartDateSelected"
                    :outlined="outlinedInputs"
                  ></date-picker>
                </v-flex>
                <v-flex xs12 sm4 v-show="!isActivity">
                  <date-picker
                    :customLabel="endDateLabel"
                    :gt-date-string="endDate"
                    :rangeStart="endDateRangeStart"
                    :rangeEnd="endDateRangeEnd"
                    @onDateSelected="onEndDateSelected"
                    :outlined="outlinedInputs"
                  ></date-picker>
                </v-flex>
                <v-flex v-show="!isActivity">
                  <p class="title py-0 pl-2">{{ durationLabel }}</p>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs2>
                  <v-text-field
                    label="Adults"
                    type="number"
                    v-model="adults"
                    :max="maxAdults"
                    min="0"
                    @change="updateQty"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs2 v-if="!hideChildren">
                  <v-text-field
                    label="Children"
                    type="number"
                    v-model="children"
                    :max="maxChildren"
                    min="0"
                    @change="updateQty"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs2 v-if="!hideChildren">
                  <v-text-field
                    label="Infants"
                    type="number"
                    v-model="infants"
                    @change="updateQty"
                    :max="maxInfants"
                    min="0"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs2>
                  <v-text-field
                    label="Qty"
                    type="number"
                    v-model="qty"
                    min="1"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs4>
                  <service-status-list-select
                    :service-status-id="statusId"
                    :service-status-name="statusName"
                    @onSelect="onServiceStatusChange($event)"
                    :is-disabled="isExternal || isCancelled"
                    :outlined="outlinedInputs"
                  ></service-status-list-select>
                </v-flex>
              </v-layout>
              <v-layout row wrap></v-layout>

              <item-linked-services-list
                v-if="serviceLinkedServicesIds.length > 0"
                :item="selectedItem"
                :services-ids="serviceLinkedServicesIds"
                :service-id="serviceId"
                :start-date="startDate"
                :end-date="endDate"
                :adults="adults"
                :children="children"
                :infants="infants"
                :qty="qty"
                :service-type="serviceType"
                :department="headerProps.departmentName"
                :agent-id="headerProps.agentId"
                :destination-id="headerProps.destinationId"
                :header-props="headerProps"
                :rate-plan="ratePlan"
                :item-linked-services="linkedServices"
                @save="onLinkedServiceSave"
              ></item-linked-services-list>
              <item-available-extras-list
                v-if="!isExternal"
                :service-id="serviceId"
                :from="startDate"
                :to="endDate"
                :adults="adults"
                :children="children"
                :infants="infants"
                :qty="qty"
                :service-type="serviceType"
                @on-selected-extras-change="onSelectedExtrasChange"
                :extras="extras"
                :key="extrasRefreshKey"
              ></item-available-extras-list>
              <v-row v-if="isCustomPrice" justify="end">
                <h6 class="headline green--text font-weight-light">
                  Custom Price
                </h6>
              </v-row>
              <item-total-price-info
                v-else
                :service-id="serviceId"
                :agent-id="headerProps.agentId"
                :adults="adults"
                :children="children"
                :infants="infants"
                :qty="qty"
                :created-at="headerProps.createdAt"
                :start-date="startDate"
                :end-date="endDate"
                :get-price="getPriceBreakDown"
                :extras="extras"
                :department="headerProps.departmentName"
                :ratePlan="ratePlan"
                @onLoad="onPriceLoaded"
                @onLoadError="onGetPriceError"
                :skip="!show"
                :adjustment="linkedServicesTotalPrice"
              ></item-total-price-info>
            </v-container>
          </v-tab-item>
          <v-tab-item key="remarks" v-if="selectedItem">
            <item-remarks
              v-model="selectedItem.remarks"
              :service-type="serviceType"
            />
          </v-tab-item>
        </v-tabs-items>

        <div v-if="debug">
          <h1>linkedServicesTotalPrice</h1>
          <code>{{ linkedServicesTotalPrice }}</code>
          <h1>linkedServices: {{ linkedServices.length }}</h1>
          <code>{{ linkedServices }}</code>
        </div>
        <div v-if="serviceNotesStr != ''">
          <v-alert type="info" transition="scale-transition"
            ><div v-html="serviceNotesStr"></div
          ></v-alert>
        </div>
        <div v-if="isExternal && (successAlert || errorAlert)">
          <v-alert
            :value="successAlert"
            type="success"
            transition="scale-transition"
            >{{ successAlertMsg }}</v-alert
          >
          <v-alert
            :value="errorAlert"
            type="error"
            transition="scale-transition"
            >{{ errorAlertMsg }}</v-alert
          >
        </div>
        <v-alert
          :value="flightErrorMsg != ''"
          type="error"
          transition="scale-transition"
          >{{ flightErrorMsg }}</v-alert
        >
      </v-card-text>

      <v-card-actions class="grey lighten-4">
        <v-btn
          v-if="isExternal && !isExtAndConfirmed"
          :disabled="rezGraphBookBtnDisabled"
          :loading="rezGraphLoading"
          color="success"
          @click="onBook"
          >{{ bookFromLabel }}</v-btn
        >
        <v-btn
          v-if="isExtAndConfirmed && !isCancelled"
          :disabled="rezGraphCancelBtnDisabled"
          :loading="rezGraphLoading"
          color="warning"
          @click="onCancelExtBooking"
          >{{ cancelExtBookingLabel }}</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn color="warning" :disabled="cancelDisabled" text @click="onCancel"
          >Close</v-btn
        >

        <v-btn
          color="secondary"
          :disabled="saveDisabled"
          text
          @click="onSaveAndContinue"
          >{{
            isNewItem ? "Add &amp; Continue" : "Update &amp; Continue"
          }}</v-btn
        >
        <v-btn
          color="secondary"
          :disabled="saveDisabled"
          text
          @click="onSaveAndNextDate"
          >{{
            isNewItem ? "Add &amp; Next Date" : "Update &amp; Next Date"
          }}</v-btn
        >
        <v-btn color="primary" :disabled="saveDisabled" @click="onSave">{{
          isNewItem ? "Add" : "Update"
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DatePicker from "../../shared/DatePicker";
import ServiceStatusListSelect from "../../shared/ServiceStatusListSelect";
import ItineraryBuilderItemQuickAdd from "./ItineraryBuilderItemQuickAdd";
import ItemTotalPriceInfo from "./tools/ItemTotalPriceInfo";
import ItemAvailableExtrasList from "./tools/ItemExtrasList";
import ItemLinkedServicesList from "./tools/ItemLinkedServicesList.vue";
import ItemRemarks from "./tools/ItemRemarks";
import EventBus from "../../../../store/event-bus";
import { BOOKING_QUERY } from "../../../../constants/rezGraphQL";
import { SERVICE_NOTES } from "./tools/adhoc-item-gql.js";
import {
  ALL_RATE_TYPES_SEARCH_QUERY,
  FLIGHT_LOOKUP,
} from "../../../../constants/graphql";
import {
  removeGQLTypename,
  durationInDays,
  stripHtml,
} from "../../../../helpers";
import gql from "graphql-tag";

const BOOK_ONLINE = gql`
  mutation createOnlineBooking($input: BookOnlineInput!) {
    bookOnline(input: $input) {
      success
      error
      message
      confirmationRef
    }
  }
`;
const BOOK_ONLINE_MULTIPLE = gql`
  mutation createMultiOnlineBooking($input: [BookOnlineInput]) {
    bookOnlineMultiple(input: $input) {
      success
      error
      message
      confirmationRef
    }
  }
`;
const CANCEL_ONLINE = gql`
  mutation cancelBooking($input: CancelOnlineInput!) {
    cancelOnline(input: $input) {
      success
      message
      error
    }
  }
`;
const moment = require("moment");
const randomize = require("randomatic");
const SERVICE_INFO = gql`
  query serviceInfo($id: ID!) {
    service(id: $id) {
      id
      extrasChildPrice
      hiddenByType
      skipAgentCommission
      pickupPoint
      dropoffPoint
      pickupTime
      dropoffTime
      noReport
      linkedServices {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  }
`;
const SUPPLIER_ITINERARY_NOTES = gql`
  query supplierItineraryNotes($itineraryId: ID!) {
    supplierItineraryNotes(itineraryId: $itineraryId) {
      edges {
        node {
          id
          itineraryId
          name
          code
          content
          supplierIds
          supplierNames
          forConfirm
          forConfirmation
          forAmendment
          forCancel
          forQuotation
          forVoucher
          forOther
          forWeb
        }
      }
    }
  }
`;
export const EXTERNAL_ITEM_PRICING = gql`
  query rsResultServicePricing(
    $serviceId: ID!
    $rsResultServiceId: ID
    $startDate: DateWithTimeType!
    $endDate: DateWithTimeType!
    $adults: Int!
    $children: Int!
    $infants: Int!
    $qty: Int!
    $agentId: ID
    $department: String
    $ratePlan: String
    $extras: [ItineraryItemExtraInput]
    $yearShift: Int
  ) {
    externalItemPricing: getPricing(
      serviceId: $serviceId
      rsResultServiceId: $rsResultServiceId
      agentId: $agentId
      department: $department
      ratePlan: $ratePlan
      adults: $adults
      children: $children
      infants: $infants
      qty: $qty
      startDate: $startDate
      endDate: $endDate
      extras: $extras
      yearShift: $yearShift
    ) {
      adultBuy
      adultSell
      childBuy
      childSell
      infantBuy
      infantSell
      itemBuy
      itemSell
      rateTypeCode
      ratePlan
      currencySell
      currencySellExchangeRate
      currencyBuy
      currencyBuyExchangeRate
      rateTypeCode
      totalSell
      serviceSell
      discount
      businessModel
      supplierCommission
      message
      extras {
        id
        name
        uid
        totalSell
        totalBuy
        isChildOnly
        isInfantOnly
        rateTypeCode
        rateBase
        adults
        children
        infants
        cost
        sell
        percentPrice
        customPricing
        isMandatory
        discount
        discountCost
        discountSell
        currencyBuyExchangeRate
        currencySellExchangeRate
      }
    }
  }
`;
export default {
  name: "ItineraryBuilderItemsListItemEditor",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedItem: {
      type: Object,
      default: () => null,
    },
    allProviderItems: {
      type: Array,
      default: () => [],
    },
    headerProps: {
      type: Object,
    },
    participants: {
      type: Array,
      default: () => [],
    },
    isNewItem: {
      type: Boolean,
      default: () => true,
    },
  },
  components: {
    DatePicker,
    ItineraryBuilderItemQuickAdd,
    ServiceStatusListSelect,
    ItemTotalPriceInfo,
    ItemAvailableExtrasList,
    ItemRemarks,
    ItemLinkedServicesList,
  },
  data() {
    return {
      debug: false,
      flightErrorMsg: "",
      currentTab: 0,
      itemPricing: null,
      switchService: false,
      focusOnQuickAdd: false,
      label: "Item Options",
      startDate: "",
      endDate: "",
      adults: "0",
      children: "0",
      infants: "0",
      qty: "1",
      stdOccupancy: 0,
      serviceName: "",
      supplierName: "",
      serviceType: this.selectedItem ? this.selectedItem.serviceType : "",
      serviceId: "",
      price: 0,
      currencyCode: "",
      rateTypeCode: "",
      ratePlan: "",
      statusId: this.selectedItem ? this.selectedItem.statusId : "",
      status: "Quotation",
      selected: false,
      propNames: [
        "serviceName",
        "supplierName",
        "serviceType",
        "serviceId",
        "startDate",
        "endDate",

        "price",
        "currencyCode",
        "rateTypeCode",
        "ratePlan",
        "statusId",
        "status",
        "selected",
      ],
      startDateRangeStart: this.headerProps.startDate,
      startDateRangeEnd: this.headerProps.endDate,
      endDateRangeStart: this.headerProps.startDate,
      endDateRangeEnd: this.headerProps.endDate,
      source: { id: "", name: "GTWEB" },
      pricing: null,
      rezGraphBookToken: "",
      skipBookingQuery: true,
      rezGraphBookingDetails: {},
      rezGraphBookBtnDisabled: false,
      rezGraphCancelBtnDisabled: false,
      rezGraphCancellation: false,
      rezGraphLoading: false,
      bookFromLabel: "Book Online",
      errorAlert: false,
      errorAlertMsg: "",
      successAlert: false,
      successAlertMsg: "",
      cancelDisabled: false,
      saveDisabled: false,
      statusName: "Quotation",
      getPriceBreakDown: false,
      currentPricingItem: null,
      currentPricingItemIndex: 0,
      pricesChecked: false,
      priceCheckObj: {},
      extras:
        this.selectedItem &&
        this.selectedItem.hasOwnProperty("extras") &&
        this.selectedItem.extras != undefined
          ? [...this.selectedItem.extras]
          : [],
      remarks: {},
      extrasRefreshKey: randomize("Aa0", 32),
      linkedServices:
        this.selectedItem && this.selectedItem.linkedServices
          ? this.selectedItem.linkedServices
          : [],
    };
  },

  methods: {
    onLinkedServiceSave(item) {
      console.log("onLinkedServiceSave", item);
      //check if item is already in linkedServices
      const index = this.linkedServices.findIndex(
        (service) => service.serviceId === item.serviceId
      );
      if (index > -1) {
        this.linkedServices[index] = { ...item };
      } else {
        this.linkedServices.push(item);
      }
    },
    onRemarks() {
      if (!this.isNewItem) {
        setTimeout(() => {
          this.currentTab = 0;
          this.$emit("show-remarks-editor");
        }, 50);
      }
    },
    addTimeToDates(pickupTime, dropoffTime) {
      const [ph, pm] = pickupTime.split(":");
      const [dh, dm] = dropoffTime.split(":");
      const newStartDate = moment(this.startDate)
        .hour(ph)
        .minute(pm)
        .toISOString(true);
      //console.log("adding time", this.startDate, newStartDate);
      this.startDate = newStartDate;
      const newEndDate = moment(this.endDate)
        .hour(dh)
        .minute(dm)
        .toISOString(true);
      this.endDate = newEndDate;
      //console.log("adding time", this.endDate, newEndDate);
      // this.startDate = moment(this.startDate)
      //   //.utcOffset(0, true)
      //   .hour(ph)
      //   .minute(pm)
      //   .toISOString(true);
      // this.endDate = moment(this.endDate)
      //   .utcOffset(0, true)
      //   .hour(dh)
      //   .minute(dm)
      //   .format();
    },
    checkDuration() {
      //http://tracker.globetrack.ie/issues/5905
      // only if both pickup and dropoff times are set
      if (
        this.selectedItem &&
        this.selectedItem.remarks.pickupTime &&
        this.selectedItem.remarks.pickupTime != "" &&
        this.selectedItem.remarks.dropoffTime &&
        this.selectedItem.remarks.dropoffTime != ""
      ) {
        const pickupTime =
          this.selectedItem &&
          this.selectedItem.remarks.pickupTime &&
          this.selectedItem.remarks.pickupTime != ""
            ? this.selectedItem.remarks.pickupTime
            : "00:00";
        const dropoffTime =
          this.selectedItem &&
          this.selectedItem.remarks.dropoffTime &&
          this.selectedItem.remarks.dropoffTime != ""
            ? this.selectedItem.remarks.dropoffTime
            : "00:01";
        const differentTimes =
          pickupTime != this.remarks.pickupTime ||
          dropoffTime != this.remarks.dropoffTime;
        if (this.serviceType == "TRANSPORT" && differentTimes) {
          this.addTimeToDates(pickupTime, dropoffTime);
          this.remarks = { ...this.selectedItem.remarks };
          this.getPriceBreakDown = true;
        }
      } else {
        this.remarks = { ...this.selectedItem.remarks };
        this.addTimeToDates("00:00", "00:01");
        this.getPriceBreakDown = true;
      }
    },
    onSupplierEdit(payload) {
      this.$emit("supplier-edit", payload);
    },
    showAdhocFromQuickAdd(payload) {
      this.$emit("showAdhoc", payload);
    },
    onGetPriceError(payload) {
      this.saveDisabled = true;
    },
    onPriceLoaded(payload) {
      this.saveDisabled = false;
      //console.log("new price", payload);
      this.itemPricing = { ...payload };
      if (this.selectedItem.headless) {
        this.onSave();
      }
    },
    onExtBookingDetails() {
      //console.log("getting booking details");
    },

    bookService(index) {
      this.currentPricingItem = this.getPricingParamsFor(
        this.notCancelled[index]
      );
      console.log("this.currentPricingItem", index, this.currentPricingItem);
    },
    bookEachService() {
      this.rezGraphLoading = true;
      this.currentPriceCheckIndex = 0;
    },
    checkExternalPrice(index) {
      this.currentPricingItem = this.getPricingParamsFor(
        this.notCancelled[index]
      );
      console.log("this.currentPricingItem", index, this.currentPricingItem);
    },
    processExternalItemPricing(data) {
      const checked = this.notCancelled[this.currentPriceCheckIndex];
      this.currentPriceCheckIndex += 1;
      console.log("processExternalItemPricing", data);
      this.priceCheckObj[checked.id] = data;
      console.log(
        "this.currentPriceCheckIndex < this.notCancelled.length",
        this.currentPriceCheckIndex,
        this.notCancelled.length
      );
      if (this.currentPriceCheckIndex < this.notCancelled.length) {
        this.checkExternalPrice(this.currentPriceCheckIndex);
      } else {
        this.rezGraphLoading = false;
        this.currentPriceCheckIndex = 0;
        console.log("this.priceCheckObj", this.priceCheckObj);
        const changed = [];
        this.notCancelled.forEach((item) => {
          const pricing = this.priceCheckObj[item.id];
          if (
            pricing &&
            this.externalItemPriceHasChangedFor(
              item,
              pricing.externalItemPricing
            )
          ) {
            changed.push(
              `${item.serviceName} price has changed from ${item.price} to ${pricing.externalItemPricing.totalSell}`
            );
          }
        });
        if (changed.length == 0) {
          this.pricesChecked = true;
          this.onBook();
        } else {
          alert(
            `${changed.join(
              "\n"
            )}\nPlease update price in service pricing screen and try again.`
          );
        }
      }
    },
    checkAllPrices() {
      this.rezGraphLoading = true;
      this.currentPriceCheckIndex = 0;
      this.checkExternalPrice(this.currentPriceCheckIndex);
    },
    async onCancelExtBooking() {
      this.rezGraphLoading = true;
      const cancelRes = await this.$apollo.mutate({
        mutation: CANCEL_ONLINE,
        variables: {
          input: {
            id: this.source.id,
            bookingId: this.selectedItem.remarks.confirmationNo,
            source: this.source.name,
          },
        },
      });
      if (cancelRes.data.cancelOnline.success) {
        this.$store.dispatch("showAppSnackbar", {
          msg: cancelRes.data.cancelOnline.message,
        });
        this.successAlertMsg = cancelRes.data.cancelOnline.message;
        this.successAlert = true;
        this.rezGraphLoading = false;
        this.cancelDisabled = true;
        this.statusName = "Cancelled with no charge";
        this.status = "Cancelled with no charge";
        this.statusId = "U2VydmljZVN0YXR1cy00";
        const log = {
          message: `(${this.source.name}) ${this.supplierName} - ${
            this.serviceName
          } ${
            this.extras.length > 0
              ? `(Extras: ${this.extras.map((e) => e.name).join(", ")})`
              : ""
          } | Cancelled  ref: ${this.source.confirmationRef}`,
          marker: "EXT_CX",
        };
        EventBus.$emit("add-iti-log", log);
        delete this.source.confirmationRef;
        this.$emit("onSave", { data: this.getData(), action: "save-it" });
      } else {
        this.$store.dispatch("showAppSnackbar", {
          msg: cancelRes.data.cancelOnline.error,
          color: "error",
        });

        this.rezGraphLoading = false;
      }
      // this.rezGraphBookToken = this.source.id;
      // this.rezGraphLoading = true;
      // const cancelBookRes = await this.$apollo.mutate({
      //   client: "rezGraph",
      //   mutation: CANCEL_MUTATION,
      //   variables: {
      //     token: this.rezGraphBookToken,
      //   },
      // });
      // if (!cancelBookRes.data.cancelled) {
      //   this.rezGraphCancellation = true;
      //   this.skipBookingQuery = false;
      // }
    },
    async onBook() {
      const checkPricesBeforeBooking =
        this.source && this.source.name == "BENTRAL";
      if (checkPricesBeforeBooking && !this.pricesChecked) {
        console.log("check prices before booking");
        return await this.checkAllPrices();
      }
      if (this.source && this.source.name == "GTWEB_MP") {
        this.statusName = "Requested";
        this.status = "Requested";
        this.statusId = "U2VydmljZVN0YXR1cy0y";
        this.successAlertMsg = "Status set to Requested";
        this.successAlert = true;
        //update linked services
        if (this.linkedServices && this.linkedServices.length > 0) {
          this.linkedServices.forEach((linkedService) => {
            linkedService.status = "Requested";
            linkedService.statusId = "U2VydmljZVN0YXR1cy0y";
          });
        }
      } else {
        if (this.validateForBook()) {
          this.rezGraphCancelBtnDisabled = true;
          this.rezGraphLoading = true;
          this.cancelDisabled = true;
          let mutation = BOOK_ONLINE;
          let input = {
            id: this.source.id,
            agentId: this.headerProps.agentId,
            itineraryId: this.headerProps.id,
            startDate: this.startDate.split("T")[0],
            endDate: this.endDate.split("T")[0],
            paxNames: this.rezGraphPaxNames(),
            adults: Number(this.adults),
            children: Number(this.children),
            infants: Number(this.infants),
            source: this.source.name,
            rsResultServiceId: this.selectedItem.source.rsResultServiceId,
            notes: this.notesForSupplier,
            extras: this.extras.map((e) => ({
              id: e.id,
              uid: e.uid,
              startDate: e.startDate,
              endDate: e.endDate,
              //https://tracker.globetrack.ie/issues/8374
              qty: e.rateBase == "P" ? e.adults : e.qty,
            })),
          };
          if (this.bookMultiple) {
            if (this.notCancelled.length == 0) {
              alert("Cannot book a cancelled item!");
              this.rezGraphLoading = false;
              this.cancelDisabled = false;
              return;
            }
            mutation = BOOK_ONLINE_MULTIPLE;
            input = this.notCancelled.map((item) => {
              return {
                id: item.source.id,
                agentId: this.headerProps.agentId,
                startDate: item.startDate.split("T")[0],
                endDate: item.endDate.split("T")[0],
                paxNames: this.rezGraphPaxNames(),
                adults: Number(item.adults),
                children: Number(item.children),
                infants: Number(item.infants),
                qty: Number(item.qty),
                source: item.source.name,
                notes: this.notesForSupplier,
                special: JSON.stringify({
                  ratePlan: item.ratePlan,
                  itemId: item.id,
                }),
                extras: item.extras
                  ? item.extras.map((e) => ({
                      id: e.id,
                      uid: e.uid,
                      startDate: e.startDate,
                      endDate: e.endDate,
                      //https://tracker.globetrack.ie/issues/8374
                      qty: e.rateBase == "P" ? e.adults : e.qty,
                    }))
                  : [],
              };
            });
          }
          //          console.log("input", input, "mutation", mutation);
          try {
            const bookRes = await this.$apollo.mutate({
              mutation,
              variables: {
                input,
              },
            });
            const resp =
              bookRes.data.bookOnline || bookRes.data.bookOnlineMultiple;
            if (resp.success) {
              this.$store.dispatch("showAppSnackbar", {
                msg: resp.message,
              });
              //
              const confRef = resp.confirmationRef;
              //this.source.id = bookingId;
              this.source.confirmationRef = confRef;
              this.bookFromLabel = `Booked with ref: ${confRef}`;
              this.statusName = "Confirmed";
              this.status = "Confirmed";
              this.statusId = "U2VydmljZVN0YXR1cy0z";
              this.successAlertMsg = resp.message;
              this.successAlert = true;
              //disable btns
              this.rezGraphBookBtnDisabled = true;
              this.cancelDisabled = true;
              this.rezGraphCancelBtnDisabled = false;
              //add log
              const log = {
                message: `(${this.source.name}) ${this.supplierName} - ${
                  this.serviceName
                } ${
                  this.extras.length > 0
                    ? `(Extras: ${this.extras.map((e) => e.name).join(", ")})`
                    : ""
                } | Booked with ref: ${confRef}`,
                marker: "EXT_CF",
              };
              //EventBus.$emit("add-iti-log", log);
              //this.$emit("external-confirmed", { ...resp });
              //this.$emit("onSave", { data: this.getData(), action: "save-it" });
            } else {
              this.$store.dispatch("showAppSnackbar", {
                msg: resp.error,
                color: "error",
              });
              this.cancelDisabled = false;
            }
            this.rezGraphLoading = false;
          } catch (error) {
            console.log(error);
            const errorMsg = error.graphQLErrors
              .map((e) => e.message)
              .join(", ");
            this.$store.dispatch("showAppSnackbar", {
              msg: errorMsg,
              color: "error",
            });

            this.rezGraphLoading = false;
            this.cancelDisabled = false;
          }
        }
      }
    },
    rezGraphPaxNames() {
      const leader = this.participants[0];
      return [
        {
          title: leader.title,
          name: leader.name,
          surname: leader.surname,
          mobile: leader.mobile,
          email: leader.email,
        },
      ];
    },
    validateForBook() {
      console.log("skipExternalPriceCheck", this.skipExternalPriceCheck); //do not remove this - otherwise source.name might not be set correctly!
      if (this.participants.length > 0) {
        const leader = this.participants[0];
        for (let prop of ["name", "surname"]) {
          //, "email", "mobile" removed as per skype conversation with Rob and Christina on 05/10 @ 12:40
          if (leader[[prop]] == "") {
            alert(`Cannot book with client without "${prop}"`);
            return false;
          }
        }
      } else {
        alert("Cannot book without participant info!");
        return false;
      }
      /*
      if (!this.externalItemIsAvailable && !this.skipExternalPriceCheck) {
        alert("Cannot book an unavailable item!");
        return false;
      }
      if (!this.externalExtrasAreAvailable && !this.skipExternalPriceCheck) {
        alert("Cannot book an unavailable extra!");
        return false;
      }
      if (this.externalItemPriceHasChanged && !this.skipExternalPriceCheck) {
        alert("Cannot book an item with price change!");
        return false;
      }
      */
      return true;
    },
    onCancel() {
      //this.show = false;
      this.switchService = false;
      this.focusOnQuickAdd = false;
      this.$emit("onCancel");
    },
    getData() {
      const data = { id: this.selectedItem.id };
      //pax info
      data.adults = this.adults;
      data.children = this.extrasChildPrice ? 0 : this.children;
      data.infants = this.extrasChildPrice ? 0 : this.infants;
      data.qty = this.qty;
      for (let field of this.propNames) {
        data[field] = this[field];
      }
      //update dates based on service type
      if (this.isActivity) {
        data.endDate = data.startDate;
      }

      // add external source info
      if (this.source.id != "") {
        data.source = this.source;
      }
      //add custom price section
      if (this.pricing) {
        data.pricing = this.pricing;
      }
      //add extras
      data.extras = this.updatedExtrasInfo();
      if (this.itemPricing) {
        //add pricebrekdown
        const {
          totalSell,
          totalBuy,
          serviceSell,
          __typename,
          businessModel,
          supplierCommission,
          serviceNotes,
          ...pricing
        } = this.itemPricing;
        //data.price = totalSell;
        data.price = serviceSell;
        data.businessModel = businessModel;
        data.supplierCommission = supplierCommission;
        //console.log("total sell from pricing", totalSell);

        data.pricing = pricing;
      }
      //console.log("data from editor", data);
      //refresh remarks

      data.remarks = { ...this.selectedItem.remarks };

      //add serviceNotes to remarks - only if user has it on on access settings
      if (
        this.serviceNotesStr != "" &&
        !this.$store.getters.hasAccessTo("itineraries:hide_notes_from_remarks")
      ) {
        data.remarks.agentNotes = data.remarks.agentNotes
          ? ` ${data.remarks.agentNotes}`
          : this.serviceNotesStr.replace("<br>", "\n");
      }

      if (this.source.confirmationRef && this.source.confirmationRef != "") {
        //data.remarks.voucherNotes = this.source.confirmationRef;
        data.remarks.confirmationNo = this.source.confirmationRef;
      }
      data.stdOccupancy = this.stdOccupancy;
      data.isHidden = this.service ? this.service.noReport : false;
      data.includeCommission = !this.service
        ? true
        : !this.service.skipAgentCommission;

      data.linkedServices = this.linkedServices.map((item) => ({
        parentId: this.selectedItem.id,
        ...item,
      }));
      return data;
    },
    onSave() {
      //console.log("this.getData()", this.getData());
      this.$emit("onSave", { data: this.getData(), action: "close" });
    },
    onSaveAndContinue() {
      this.$emit("onSave", { data: this.getData(), action: "continue" });
      //reset extras
      this.extras = [];
      //create new item id
      this.selectedItem.id = randomize("Aa0", 32);
      this.switchService = true;
      this.focusOnQuickAdd = true;
      this.saveDisabled = true;
    },
    onSaveAndNextDate() {
      this.$emit("onSave", { data: this.getData(), action: "next_date" });
      //reset extras
      this.extras = [];
      //create new item id
      this.selectedItem.id = randomize("Aa0", 32);
      //and update dates
      const newStartDate =
        this.serviceType == "ACCOMMODATION"
          ? moment(this.endDate)
          : moment(this.startDate).add(1, "day");

      const defaulDuration = this.serviceType == "ACCOMMODATION" ? 1 : 0;
      const newEndDate = moment(newStartDate).add(defaulDuration, "day");
      //console.log(newStartDate, newEndDate);

      if (newStartDate <= moment(this.headerProps.endDate)) {
        this.startDate = newStartDate.toISOString(true);
      }
      if (newEndDate <= moment(this.headerProps.endDate)) {
        this.endDate = newEndDate.toISOString(true);
      } else {
        this.endDate = this.headerProps.endDate;
      }
      this.switchService = true;
      this.focusOnQuickAdd = true;
      this.saveDisabled = true;
    },
    onStartDateSelected($event) {
      this.startDate = $event;
      this.endDateRangeStart = $event;
      if (this.isActivity) {
        this.endDate = this.startDate;
      }
      this.updateDates();
    },
    onEndDateSelected($event) {
      this.endDate = $event;
      //this.startDateRangeEnd = $event;
      this.updateDates();
    },
    updateDates() {
      const start = moment(this.startDate);
      const end = moment(this.endDate);
      if (start > end) {
        this.endDate = start.toISOString(true);
      }
      if (this.extras.length > 0) {
        for (const extra of this.extras) {
          const extraStart = moment(extra.startDate);
          const extraEnd = moment(extra.endDate);
          const isOutsideRange =
            !extraStart.isBetween(start, end, "days", "[]") ||
            !extraEnd.isBetween(start, end, "days", "[]");
          if (isOutsideRange) {
            extra.startDate = start.toISOString(true);
            extra.endDate =
              this.serviceType &&
              this.serviceType == "ACCOMMODATION" &&
              !start.isSame(end, "day")
                ? moment(end).subtract(1, "day").toISOString(true)
                : start.toISOString(true);
          }
          // if (extra.customPricing) {
          //   extra.customPricing = false;
          // }
        }
      }
      if (
        this.selectedItem &&
        this.selectedItem.remarks.pickupTime &&
        this.selectedItem.remarks.pickupTime != "" &&
        this.selectedItem.remarks.dropoffTime &&
        this.selectedItem.remarks.dropoffTime != ""
      ) {
        const pickupTime =
          this.selectedItem &&
          this.selectedItem.remarks.pickupTime &&
          this.selectedItem.remarks.pickupTime != ""
            ? this.selectedItem.remarks.pickupTime
            : "00:00";
        const dropoffTime =
          this.selectedItem &&
          this.selectedItem.remarks.dropoffTime &&
          this.selectedItem.remarks.dropoffTime != ""
            ? this.selectedItem.remarks.dropoffTime
            : "00:01";
        this.addTimeToDates(pickupTime, dropoffTime);
      } else {
        this.addTimeToDates("00:00", "00:01");
      }
      if (this.isCustomPrice) {
        this.updateCustomPrice();
      }
    },
    updateCustomPrice() {
      const rateType = this.rateTypes.edges
        .map(removeGQLTypename)
        .find((item) => item.code == this.rateTypeCode);
      if (rateType) {
        let total = 0;
        if (rateType.includePax) {
          total =
            Number(this.selectedItem.adults) *
              Number(this.selectedItem.pricing.adultSell) +
            Number(this.selectedItem.children) *
              Number(this.selectedItem.pricing.childSell) +
            Number(this.selectedItem.infants) *
              Number(this.selectedItem.pricing.infantSell);
        } else {
          total =
            Number(this.selectedItem.qty) *
            Number(this.selectedItem.pricing.itemSell);
        }
        if (rateType.includeDays) {
          // let duration = Number(durationInDays(this.startDate, this.endDate));
          // if (this.selectedItem.serviceType != "ACCOMMODATION") {
          //   duration++;
          // }
          total = total * this.duration;
        }
        this.price = total;
      }
    },
    onServiceSelect(payload) {
      //      console.log("onServiceSelect",payload);
      if (payload != undefined) {
        this.label = `${payload.supplierName} - ${payload.serviceName}`;
        for (const field of this.propNames) {
          if (field.indexOf("Date") == -1) {
            if (payload[field]) {
              this[field] = payload[field];
            }
          }
        }
        //set ratePlan
        if (payload.prices && payload.prices.length > 0) {
          this.ratePlan =
            payload.prices[0].ratePlan != ""
              ? payload.prices[0].ratePlan
              : null;
        }
        this.switchService = false;
        this.saveDisabled = false;
      }
    },
    onServiceEdit() {
      // new way - show adhoc
      // pass recalculated qty so AddAdhocItem opens with the correct value (#11109)
      this.$emit("showAdhocFromEdit", { ...this.selectedItem, qty: this.qty });
      /*this.switchService = true;
      this.focusOnQuickAdd = true;*/
    },
    onServiceStatusChange($event) {
      this.statusId = $event.id;
      this.status = $event.name;
    },
    onSelectedExtrasChange(payload) {
      //console.log("onSelectedExtrasChange", payload);

      this.extras = [...payload];
    },
    updateQty() {
      //console.log("updating qty");
      if (this.headerProps) {
        // if (parseInt(this.adults) > parseInt(this.headerProps.adults)) {
        //   this.adults = this.headerProps.adults;
        // }

        // if (parseInt(this.children) > parseInt(this.headerProps.children)) {
        //   this.children = this.headerProps.children;
        // }

        if (parseInt(this.infants) > parseInt(this.headerProps.infants)) {
          this.infants = this.headerProps.infants;
        }
      }

      const selectedPax = this.extrasChildPrice
        ? parseInt(this.adults)
        : parseInt(this.adults) + parseInt(this.children);
      //console.log("updating qty", selectedPax, this.stdOccupancy);
      if (this.stdOccupancy == undefined || this.stdOccupancy == 0) {
        this.qty = "1";
        return;
      }

      this.qty = Math.ceil(selectedPax / this.stdOccupancy).toString();
    },
    updatedExtrasInfo() {
      const updated = [
        ...this.extras.map((extra) => {
          if (!extra.statusId) {
            extra.statusId = "U2VydmljZVN0YXR1cy0x";
            extra.status = "Quotation";
          }
          //extra status for external provider extras
          extra.isIncluded = true;
          extra.isOptional = false;
          if (this.isExternal) {
            extra.statusId = this.statusId;
            extra.status = this.statusName;
          }
          const extraPricingInfo = this.itemPricing
            ? this.itemPricing.extras.find((e) => {
                const eKey = e.id.includes(e.name)
                  ? `${e.name}:::${e.id}`
                  : e.id; //http://tracker.globetrack.ie/issues/6146
                const extraKey = extra.id.includes(extra.name)
                  ? `${extra.name}:::${extra.id}`
                  : extra.id; //http://tracker.globetrack.ie/issues/6146
                return eKey == extraKey;
              })
            : null;
          const rateBase = extraPricingInfo
            ? extraPricingInfo.rateBase
            : extra.rateBase;
          //update qty based on rateBase
          //console.log("extrapricing info: ", extraPricingInfo);
          // if (rateBase == "P") {
          //   extra.qty = this.adults;
          // } else {
          //   extra.qty = this.qty;
          // }
          const rateType = this.rateTypes.edges
            .map(removeGQLTypename)
            .find((item) => item.code == extra.rateTypeCode);
          const duration =
            Number(durationInDays(extra.startDate, extra.endDate)) + 1;
          if (extraPricingInfo) {
            if (extra.customPricing) {
              if (rateType && rateType.includeDays) {
                return {
                  ...extra,
                  totalSell: Number(extra.sell) * Number(extra.qty) * duration,
                  totalBuy: Number(extra.buy) * Number(extra.qty) * duration,
                };
              } else {
                return {
                  ...extra,
                };
              }
            } else {
              return {
                ...extra,
                totalSell: extraPricingInfo.totalSell,
                isChildOnly: extraPricingInfo.isChildOnly,
                isInfantOnly: extraPricingInfo.isInfantOnly,
                isMandatory: extraPricingInfo.isMandatory,
                rateTypeCode: extraPricingInfo.rateTypeCode,
                rateBase: extraPricingInfo.rateBase,
                adults: extraPricingInfo.adults,
                children: extraPricingInfo.children,
                infants: extraPricingInfo.infants,
                totalBuy: extraPricingInfo.totalBuy,
                cost: extraPricingInfo.cost,
                sell: extraPricingInfo.sell,
                percentPrice: extraPricingInfo.percentPrice,
                currencyBuyExchangeRate:
                  extraPricingInfo.currencyBuyExchangeRate,
                currencySellExchangeRate:
                  extraPricingInfo.currencySellExchangeRate,
              };
            }
          } else {
            //no pricing and parent is custom priced
            //https://tracker.globetrack.ie/issues/9446
            if (this.isCustomPrice) {
              const qty =
                rateBase == "P"
                  ? extra.isChildOnly
                    ? extra.children
                    : extra.adults
                  : this.qty;
              //console.log("custom pricing", extra, rateType.includeDays, qty);
              if (rateType && rateType.includeDays) {
                return {
                  ...extra,
                  totalSell: Number(extra.sell) * Number(qty) * duration,
                  totalBuy: Number(extra.buy) * Number(qty) * duration,
                };
              } else {
                return {
                  ...extra,
                  totalSell: Number(extra.sell) * Number(qty),
                  totalBuy: Number(extra.buy) * Number(qty),
                };
              }
            }
            console.log("extra", extra);
            return extra;
          }
        }),
      ];
      //console.log("updated extras", updated);
      return updated;
    },
    externalItemPriceHasChangedFor(selectedItem, externalItemPricing) {
      // console.log(
      //   "externalItemPriceHasChangedFor",
      //   selectedItem,
      //   externalItemPricing
      // );
      return (
        externalItemPricing.adultBuy != selectedItem.pricing.adultBuy ||
        externalItemPricing.childBuy != selectedItem.pricing.childBuy ||
        externalItemPricing.infantBuy != selectedItem.pricing.infantBuy ||
        externalItemPricing.itemBuy != selectedItem.pricing.itemBuy
      );
    },
    getPricingParamsFor(item) {
      //console.log("getPricingParamsFor", item);
      return {
        serviceId: item.serviceId,
        rsResultServiceId: item.source.id,
        department: this.headerProps.departmentName,
        agentId: this.headerProps.agentId,
        ratePlan: "",
        adults: parseInt(item.adults),
        children: parseInt(item.children),
        infants: parseInt(item.infants),
        qty: parseInt(item.qty),
        startDate: item.startDate,
        endDate: item.endDate,
        extras: item.extras
          ? item.extras.map((e) => {
              let extraQty = e.qty;
              if (e.rateBase == "P") {
                if (e.isChildOnly && e.children) {
                  extraQty = Number(e.children);
                } else if (e.isInfantOnly && e.infants) {
                  extraQty = Number(e.infants);
                } else if (e.adults) {
                  extraQty = Number(e.adults);
                }
              }
              return {
                id: e.id,
                qty: extraQty,
                startDate: e.startDate,
                endDate: e.endDate,
              };
            })
          : [],
        yearShift: null,
      };
    },
  },

  computed: {
    bookMultiple() {
      return ["BENTRAL", "RESREQUEST"].indexOf(this.source.name) > -1;
    },
    linkedServicesTotalPrice() {
      return this.linkedServices
        ? this.linkedServices.reduce(
            (acc, item) =>
              acc + (item.hasOwnProperty("price") ? Number(item.price) : 0),
            0
          )
        : 0;
    },
    serviceLinkedServicesIds() {
      return this.service
        ? this.service.linkedServices.edges.map((e) => e.node.id)
        : [];
    },
    notCancelled() {
      return this.allProviderItems.filter((item) => {
        return !item.status.toString().toUpperCase().includes("CANCELLED");
      });
    },
    externalItemIsAvailable() {
      // console.log("externalItemPricing", this.externalItemPricing);
      // console.log("selectedItem", this.selectedItem);
      return this.externalItemPricing != null;
    },
    externalExtrasAreAvailable() {
      return (
        (this.externalItemPricing &&
          this.selectedItem.extras &&
          this.externalItemPricing.extras &&
          this.externalItemPricing.extras.length ==
            this.selectedItem.extras.length) ||
        !this.selectedItem.extras ||
        this.selectedItem.extras.length == 0
      );
    },
    notesForSupplier() {
      return this.supplierItineraryNotes &&
        this.supplierItineraryNotes.edges &&
        this.supplierItineraryNotes.edges.length > 0
        ? this.supplierItineraryNotes.edges
            .map(removeGQLTypename)
            .filter(
              (note) =>
                note.supplierIds.indexOf(this.selectedItem.supplierId) > -1
            )
            .map((n) => stripHtml(n.content.toString().replace(/<\/p>/g, "\n")))
            .join("\n")
        : "";
    },
    pricingParams() {
      return this.selectedItem
        ? this.getPricingParamsFor(this.selectedItem)
        : null;
    },
    serviceNotesStr() {
      return this.itemPricing && this.itemPricing.serviceNotes
        ? this.itemPricing.serviceNotes.map((sn) => sn.description).join("<br>")
        : this.serviceNotes && this.serviceNotes.edges
        ? this.serviceNotes.edges
            .map(removeGQLTypename)
            .map((sn) => sn.description)
            .join("<br>")
        : "";
    },
    maxPax() {
      return this.headerProps
        ? Number(this.headerProps.adults) + Number(this.headerProps.children)
        : 10;
    },
    maxAdults() {
      const currentPax = Number(this.adults) + Number(this.children);
      const remaining = this.maxPax - currentPax;
      return Number(this.adults) + remaining;
    },
    maxChildren() {
      const currentPax = Number(this.adults) + Number(this.children);
      const remaining = this.maxPax - currentPax;
      return Number(this.children) + remaining;
    },
    maxInfants() {
      return this.headerProps ? parseInt(this.headerProps.infants) : 1;
    },
    headless() {
      return this.selectedItem && this.selectedItem.headless;
    },
    hideChildren() {
      return this.service && this.service.extrasChildPrice;
    },
    extrasChildPrice() {
      return this.hideChildren;
    },
    isCustomPrice() {
      return this.selectedItem && this.selectedItem.customPricing;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    startDateLabel() {
      const labelsByServiceType = {
        ACCOMMODATION: "Check In",
        ACTIVITY: "Service Date",
        TRAVEL: "Departure Date",
      };
      return labelsByServiceType[this.serviceType]
        ? labelsByServiceType[this.serviceType]
        : "Start Date";
    },
    endDateLabel() {
      const labelsByServiceType = {
        ACCOMMODATION: "Check Out",
        TRAVEL: "Arrival Date",
      };
      return labelsByServiceType[this.serviceType]
        ? labelsByServiceType[this.serviceType]
        : "End Date";
    },
    isActivity() {
      return this.serviceType == "ACTIVITY";
    },
    durationLabel() {
      const label = this.serviceType == "ACCOMMODATION" ? "Night" : "Day";

      return `${this.duration} ${label}${this.duration > 1 ? "s" : ""}`;
    },
    duration() {
      //console.log(this.startDate, this.endDate);
      const start = moment(this.startDate).startOf("day");
      const end = moment(this.endDate).startOf("day");
      let duration = 1;
      if (start.isValid() && end.isValid()) {
        duration = end.diff(start, "days");
      }
      if (this.serviceType != "ACCOMMODATION") {
        if (this.serviceType == "TRANSPORT") {
          //http://tracker.globetrack.ie/issues/5905
          // only if both pickup and dropoff times are set
          if (
            this.remarks &&
            this.remarks.pickupTime &&
            this.remarks.pickupTime != "" &&
            this.remarks.dropoffTime &&
            this.remarks.dropoffTime != ""
          ) {
            const pickupTime =
              this.remarks &&
              this.remarks.pickupTime &&
              this.remarks.pickupTime != ""
                ? this.remarks.pickupTime
                : "00:00";
            const dropoffTime =
              this.remarks &&
              this.remarks.dropoffTime &&
              this.remarks.dropoffTime != ""
                ? this.remarks.dropoffTime
                : "00:01";
            if (
              Number(dropoffTime.replace(/\D/, "")) >
              Number(pickupTime.replace(/\D/, ""))
            ) {
              duration += 1;
            }
          } else {
            duration += 1;
          }
        } else {
          duration = duration + 1;
        }
      }
      if (duration == 0) duration = 1;
      return duration;
    },
    isExternal() {
      //console.log("this.source",this.source.hasOwnProperty("name"), this.source.name)
      let disabled = false;
      if (this.source.hasOwnProperty("name")) {
        if (this.source.name != "GTWEB") {
          disabled = true;
        }
      }
      return disabled;
    },
    skipExternalPriceCheck() {
      if (this.source.hasOwnProperty("name")) {
        if (this.source.name == "RESREQUEST") {
          return true;
        }
      }
      return false;
    },
    isExtAndConfirmed() {
      let confirmed = false;
      if (this.source.hasOwnProperty("confirmationRef")) {
        confirmed = this.source.confirmationRef != "";
      }
      return confirmed;
    },
    isCancelled() {
      return (
        this.statusName == "Cancelled with no charge" ||
        this.status == "Cancelled with no charge"
      );
    },
    cancelExtBookingLabel() {
      let label = "Cancel Booking: ";
      const fromSource =
        this.source.hasOwnProperty("confirmationRef") &&
        this.source.confirmationRef != ""
          ? this.source.confirmationRef
          : "";
      const fromRemarks =
        this.selectedItem.remarks &&
        this.selectedItem.remarks.confirmationNo &&
        this.selectedItem.remarks.confirmationNo.toString() != ""
          ? this.selectedItem.remarks.confirmationNo
          : "";
      //console.log("fromRemarks", fromRemarks, "fromSource", fromSource);
      let rf = fromRemarks != "" ? fromRemarks : fromSource;
      label += rf.split("\n").map((ref) => {
        return ref.indexOf(":") > -1 ? ref.split(":")[1] : ref;
      })[0];
      return label;
    },
    agentId() {
      this.$store.getters.itineraryAgentId;
    },
  },

  watch: {
    allProviderItems(items) {
      //console.log("allProviderItems", items);
    },
    selectedItem(item) {
      //console.log('data',Object.keys(this.$data));
      this.extrasRefreshKey = randomize("Aa0", 32);
      if (item != undefined) {
        //updating pax
        this.adults = item.adults.toString();
        this.children = item.children.toString();
        this.infants = item.infants.toString();
        this.qty = item.qty.toString();
        this.label = `${item.supplierName} - ${item.serviceName}`;
        for (let field of this.propNames) {
          if (item[field]) {
            this[field] = item[field].toString();
          }
        }
        if (item.hasOwnProperty("pricing")) {
          this.pricing = Object.assign({}, item.pricing);
        } else {
          this.pricing = null;
        }
        //reset item pricing
        this.itemPricing = null;
        if (item.hasOwnProperty("extras") && item.extras != undefined) {
          this.extras = [...item.extras];
        } else {
          this.extras = [];
        }
        //add remarks object for new items
        if (!item.hasOwnProperty("remarks") && this.selectedItem) {
          this.selectedItem.remarks = {};
        }
        if (
          item.hasOwnProperty("linkedServices") &&
          item.linkedServices != undefined
        ) {
          this.linkedServices = [...item.linkedServices];
        } else {
          this.linkedServices = [];
        }
        //setup source section
        if (item.hasOwnProperty("source") && item.source.name != "GTWEB") {
          this.source = Object.assign({}, item.source);
          //is already confirmed
          if (
            item.source.confirmationRef &&
            item.source.confirmationRef != ""
          ) {
            this.bookFromLabel = `Booked with ref: ${item.source.confirmationRef}`;
            this.rezGraphBookBtnDisabled = true;
          } else {
            //let countLabel = " ";
            const countLabel = this.bookMultiple
              ? ` ${this.notCancelled.length} service(s) `
              : "";
            //if (item.source.name == "RESREQUEST") {
            //  countLabel = ` ${this.allProviderItems.length} service(s) `
            //}
            this.bookFromLabel = `Book${countLabel}Online (${item.source.name.replace(
              "GTWEB_",
              ""
            )})`;
            this.rezGraphBookBtnDisabled = this.notCancelled.length == 0;
          }
        } else {
          //it's gtweb item
          this.source = { name: "GTWEB" };
          this.getPriceBreakDown = true;
        }
        this.successAlert = false;
        this.errorAlert = false;
        this.cancelDisabled = false;
        this.saveDisabled = false;
        //reset selectable date range
        //https://tracker.globetrack.ie/issues/9036
        this.startDateRangeStart = this.headerProps.startDate;
        this.startDateRangeEnd = this.headerProps.endDate;
        this.endDateRangeStart = this.headerProps.startDate;
        this.endDateRangeEnd = this.headerProps.endDate;
        // if it's empty service switch on search
        if (!item.hasOwnProperty("serviceId")) {
          //console.log("its new empty");
          this.onServiceEdit();
        }
        if (item.hasOwnProperty("service")) {
          if (item.service.stdOccupancy) {
            this.stdOccupancy = parseInt(item.service.stdOccupancy);
            this.updateQty();
          }
        }
        //set rate plan of selected price
        if (item.prices && item.prices.length > 0) {
          this.ratePlan =
            item.prices[0].ratePlan != "" ? item.prices[0].ratePlan : null;
        } else {
          this.ratePlan = item.pricing ? item.pricing.ratePlan : null;
        }
        if (item.stdOccupancy != undefined) {
          this.stdOccupancy = item.stdOccupancy;
          //this.updateQty();
        }
        this.currentTab = 0;
        this.checkDuration();
      }
    },
    headerProps(props) {
      this.startDateRangeStart = props.startDate;
      this.startDateRangeEnd = props.endDate;
      this.endDateRangeStart = props.startDate;
      this.endDateRangeEnd = props.endDate;
    },
  },
  apollo: {
    supplierItineraryNotes: {
      query: SUPPLIER_ITINERARY_NOTES,
      fetchPolicy: "network-only",
      variables() {
        return { itineraryId: this.headerProps.id };
      },
      skip() {
        return !this.show || this.headerProps.id == "" || !this.isExternal;
      },
    },
    externalItemPricing: {
      query: EXTERNAL_ITEM_PRICING,
      variables() {
        return { ...this.currentPricingItem };
      },
      manual: true,
      skip() {
        //return !this.show || !this.isExternal || this.skipExternalPriceCheck; //;
        return this.currentPricingItem == null;
      },
      result({ data, loading }) {
        if (!loading) {
          this.processExternalItemPricing(data);
        }
      },
      error(err) {
        this.errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      },
      fetchPolicy: "network-only",
    },
    serviceNotes: {
      query: SERVICE_NOTES,
      variables() {
        return {
          filter: {
            serviceId: this.serviceId,
            from: this.startDate,
            to: this.endDate,
            department: this.headerProps.departmentName,
            agentId: this.headerProps.agentId,
          },
        };
      },
      skip() {
        return (
          this.serviceId == "" ||
          !this.serviceId ||
          !this.selectedItem ||
          !this.show
        );
      },
      fetchPolicy: "network-only",
    },
    flight: {
      query: FLIGHT_LOOKUP,
      variables() {
        return {
          serviceId: this.serviceId,
          date: this.startDate,
        };
      },
      skip() {
        this.flightErrorMsg = "";
        return (
          this.serviceId == "" ||
          !this.serviceId ||
          !this.selectedItem ||
          !this.show ||
          this.selectedItem.serviceType != "TRAVEL" ||
          !this.$store.getters.hasAccessTo("plugins:flights_management")
        );
      },
      fetchPolicy: "network-only",
      result(result) {
        if (
          !result.error &&
          result.data &&
          result.data.flight &&
          this.selectedItem
        ) {
          this.flightErrorMsg = "";
          this.selectedItem.remarks.pickupPoint =
            result.data.flight.departurePoint;
          this.selectedItem.remarks.dropoffPoint =
            result.data.flight.arrivalPoint;
          this.selectedItem.remarks.pickupTime =
            result.data.flight.departureTime;
          this.selectedItem.remarks.dropoffTime =
            result.data.flight.arrivalTime;
          this.selectedItem.remarks.flightNo = result.data.flight.flightNo;
        }
      },
      error(err) {
        console.log(err);
        this.flightErrorMsg = err.graphQLErrors
          .map((e) => e.message)
          .join(", ");
      },
    },
    service: {
      query: SERVICE_INFO,
      variables() {
        return { id: this.serviceId };
      },
      skip() {
        return this.serviceId == "" || !this.serviceId;
      },
      result({ data }) {
        this.updateQty();
        //update remarks
        //console.log("service info", data);
        if (
          this.selectedItem.remarks.pickupPoint == null ||
          this.selectedItem.remarks.pickupPoint == ""
        ) {
          this.selectedItem.remarks.pickupPoint =
            data.service && data.service.pickupPoint
              ? data.service.pickupPoint
              : "";
        }
        if (
          this.selectedItem.remarks.dropoffPoint == null ||
          this.selectedItem.remarks.dropoffPoint == ""
        ) {
          this.selectedItem.remarks.dropoffPoint =
            data.service && data.service.dropoffPoint
              ? data.service.dropoffPoint
              : "";
        }
        if (
          this.selectedItem.remarks.pickupTime == null ||
          this.selectedItem.remarks.pickupTime == ""
        ) {
          this.selectedItem.remarks.pickupTime =
            data.service && data.service.pickupTime
              ? data.service.pickupTime
              : "";
        }
        if (
          this.selectedItem.remarks.dropoffTime == null ||
          this.selectedItem.remarks.dropoffTime == ""
        ) {
          this.selectedItem.remarks.dropoffTime = data.service.dropoffTime;
        }
      },
    },
    rateTypes: {
      query: ALL_RATE_TYPES_SEARCH_QUERY,
    },
    rezGraphBookingDetails: {
      client: "rezGraph",
      query: BOOKING_QUERY,
      variables() {
        return {
          token: this.rezGraphBookToken,
        };
      },
      skip() {
        return this.skipBookingQuery;
      },
      fetchPolicy: "network-only",
      result: function (res) {
        this.skipBookingQuery = true;

        const description = res.data.rezGraphBookingDetails.description;
        // check the action book/cancel
        if (this.rezGraphCancellation) {
          if (res.data.rezGraphBookingDetails.cancelled) {
            // what to do if cancellation successful?
            this.successAlertMsg = description;
            this.successAlert = true;
            this.rezGraphLoading = false;
            this.cancelDisabled = true;
            this.statusName = "Cancelled with no charge";
            this.status = "Cancelled with no charge";
            this.statusId = "U2VydmljZVN0YXR1cy00";
          } else {
            setTimeout(() => {
              this.skipBookingQuery = false;
            }, 3000);
          }
        } else {
          if (res.data.rezGraphBookingDetails.completed) {
            const bookingId = this.rezGraphBookToken;
            const pimRef = res.data.rezGraphBookingDetails.pimRef;
            if (pimRef) {
              //set msgs
              //option id becomes booking id
              this.source.id = bookingId;
              this.source.confirmationRef = pimRef;
              this.bookFromLabel = `Booked with pim ref: ${pimRef}`;
              this.statusName = "Confirmed";
              this.successAlertMsg = description;
              this.successAlert = true;
              //disable btns
              this.rezGraphBookBtnDisabled = true;
              this.rezGraphLoading = false;
              this.cancelDisabled = true;
              //this.$emit("onSave", this.getData());
            } else {
              this.errorAlertMsg = description;
              this.errorAlert = true;

              this.rezGraphBookBtnDisabled = false;
              this.rezGraphLoading = false;
              this.cancelDisabled = false;
            }
          } else {
            setTimeout(() => {
              this.skipBookingQuery = false;
            }, 3000);
          }
        }
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/ItineraryBuilderItemsListItemEditor.vue